import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useApiGet } from '../../utils/httpClientTs';
import { getPayouts } from '../../endpoints/financePayments';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import type { StripePayoutSummary } from '../../types/financePayments';
import { formatCalendarDate } from '../../utils/formatCalendarDate';

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const PaymentsPayoutsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, isLoading } = useApiGet(
    ['finance-payments-payouts', page, rowsPerPage],
    () =>
      getPayouts({
        page: page + 1,
        page_size: rowsPerPage,
      }),
    { enabled: true, retry: 1 }
  );

  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const columns: ServerSideTableColumn<StripePayoutSummary>[] = [
    {
      id: 'stripe_payout_id',
      label: 'Stripe payout',
      width: '28%',
      firstCell: true,
      sortable: false,
      render: (_v, row) => (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {row.stripe_payout_id}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      width: '12%',
      sortable: false,
      render: (_v, row) => row.status,
    },
    {
      id: 'amount',
      label: 'Amount',
      width: '12%',
      sortable: false,
      render: (_v, row) => fmtMoney(row.amount),
    },
    {
      id: 'currency',
      label: 'CCY',
      width: '8%',
      sortable: false,
      render: (_v, row) => (row.currency ? row.currency.toUpperCase() : '—'),
    },
    {
      id: 'balance_line_count',
      label: 'Lines',
      width: '10%',
      sortable: false,
      render: (_v, row) => String(row.balance_line_count),
    },
    {
      id: 'arrival_date',
      label: 'Arrival',
      width: '14%',
      sortable: false,
      render: (_v, row) => formatCalendarDate(row.arrival_date),
    },
    {
      id: 'synced_at',
      label: 'Synced',
      width: '14%',
      sortable: false,
      render: (_v, row) =>
        row.synced_at ? new Date(row.synced_at).toLocaleString() : '—',
    },
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Stripe payouts synced to the hub. Open a row for the deposit / GL
        reconciliation PDF.
      </Typography>
      <ServerSideTable<StripePayoutSummary>
        data={rows}
        columns={columns}
        loading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={pagination?.total_count ?? 0}
        totalPages={pagination?.total_pages ?? 0}
        hasNext={pagination?.has_next ?? false}
        hasPrev={pagination?.has_prev ?? false}
        order="desc"
        orderBy="arrival_date"
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
        onSort={() => {}}
        onRowClick={(row) =>
          navigate(`/finance/payments/payouts/${encodeURIComponent(row.id)}`)
        }
        emptyMessage="No Stripe payouts found yet."
      />
    </Box>
  );
};

export default PaymentsPayoutsListPage;
